import { TripParkingState, GroupedTrip } from "../types";

/**
 * Cycles trip state: unlocked -> locked -> excluded -> unlocked
 * Returns the next state in the cycle.
 */
export function cycleTripState(
  currentState: TripParkingState | undefined
): TripParkingState | undefined {
  const state = currentState || "unlocked";

  if (state === "unlocked") {
    return "locked";
  } else if (state === "locked") {
    return "excluded";
  } else {
    // excluded -> unlocked (return undefined to remove from map)
    return undefined;
  }
}

/**
 * Computes the set of selected trip IDs (non-excluded trips).
 * Selected = unlocked OR locked (anything that's not excluded)
 */
export function getSelectedTripIds(
  tripStates: Map<string, TripParkingState>,
  groupedTrips: GroupedTrip[]
): Set<string> {
  const selected = new Set<string>();
  groupedTrips.forEach((trip) => {
    const state = tripStates.get(trip.trip_id) || "unlocked";
    if (state !== "excluded") {
      selected.add(trip.trip_id);
    }
  });
  return selected;
}

/**
 * Computes the set of locked trip IDs.
 * Only trips explicitly in 'locked' state.
 */
export function getLockedTripIds(
  tripStates: Map<string, TripParkingState>
): Set<string> {
  const locked = new Set<string>();
  tripStates.forEach((state, tripId) => {
    if (state === "locked") {
      locked.add(tripId);
    }
  });
  return locked;
}

/**
 * Creates a new tripStates map with all trips set to excluded.
 */
export function excludeAllTrips(
  groupedTrips: GroupedTrip[]
): Map<string, TripParkingState> {
  const newMap = new Map<string, TripParkingState>();
  groupedTrips.forEach((trip) => {
    newMap.set(trip.trip_id, "excluded");
  });
  return newMap;
}

/**
 * Creates a new tripStates map with all non-excluded trips locked.
 */
export function lockAllTrips(
  tripStates: Map<string, TripParkingState>,
  groupedTrips: GroupedTrip[]
): Map<string, TripParkingState> {
  const newMap = new Map(tripStates);
  groupedTrips.forEach((trip) => {
    const currentState = tripStates.get(trip.trip_id) || "unlocked";
    if (currentState !== "excluded") {
      newMap.set(trip.trip_id, "locked");
    }
  });
  return newMap;
}

/**
 * Creates a new tripStates map with all locked trips set back to unlocked.
 * Preserves excluded state.
 */
export function unlockAllTrips(
  tripStates: Map<string, TripParkingState>
): Map<string, TripParkingState> {
  const newMap = new Map(tripStates);
  tripStates.forEach((state, tripId) => {
    if (state === "locked") {
      // Back to unlocked (remove from map - default is unlocked)
      newMap.delete(tripId);
    }
  });
  return newMap;
}
